import { useState, useRef, useCallback } from 'react';

// WebM-first, MP4 only as a Safari fallback (casting G8): Chrome's MP4
// MediaRecorder can't finalize incrementally and silently produces nothing.
function pickRecorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1,mp4a', 'video/mp4',
  ];
  for (const m of candidates) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* some envs throw */ } }
  return null;
}

// Chunk-streamed S3 multipart session (port of casting chunkedUpload.js) against
// the interview upload endpoints. `base` = /api/interviews/:id/upload (host) or
// /api/interview-guest/:token/upload (guest).
// Every step retries with backoff before giving up, and giving up is reported
// (onFail) instead of swallowed: a refused initiate used to mark the session
// failed silently, so the take waited forever on a clip that was never coming.
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
async function withRetry<T>(fn: () => Promise<T>, tries = 5): Promise<T> {
  let last: any;
  for (let a = 0; a < tries; a++) {
    try { return await fn(); } catch (e: any) { last = e; if (/ (401|403|404)$/.test(String(e?.message))) break; await sleep(Math.min(8000, 1000 * 2 ** a)); }
  }
  throw last;
}
function createUploadSession(base: string, headers: Record<string, string>, takeId: string, role: 'host' | 'guest', ext: string, onPct: (p: number) => void, onFail: (reason: string) => void = () => {}) {
  const MIN = 6 * 1024 * 1024;
  const st = { failed: false, uploadId: '', s3Key: '', buffer: [] as Blob[], buffered: 0, parts: [] as { PartNumber: number; ETag: string }[], next: 1, chain: Promise.resolve() as Promise<unknown>, total: 0, sent: 0 };
  const fail = (e: any) => { if (!st.failed) { console.warn('[iv upload] failed:', e?.message); st.failed = true; onFail(String(e?.message || 'upload failed')); } };
  const post = async (op: string, body: any) => {
    const c = new AbortController(); const t = setTimeout(() => c.abort(), 30000);
    try { const r = await fetch(`${base}/${op}`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: c.signal }); if (!r.ok) throw new Error(`${op} ${r.status}`); return r.json(); }
    finally { clearTimeout(t); }
  };
  const putPart = async (url: string, blob: Blob) => {
    const c = new AbortController(); const t = setTimeout(() => c.abort(), 90000);
    try { const r = await fetch(url, { method: 'PUT', body: blob, signal: c.signal }); if (!r.ok) throw new Error(`part ${r.status}`); const etag = r.headers.get('ETag'); if (!etag) throw new Error('missing ETag (bucket CORS ExposeHeaders?)'); return etag; }
    finally { clearTimeout(t); }
  };
  st.chain = withRetry(() => post('initiate', { takeId, role, ext })).then(({ uploadId, s3Key }) => { if (!uploadId || !s3Key) throw new Error('no uploadId'); st.uploadId = uploadId; st.s3Key = s3Key; }).catch(fail);
  const uploadPart = async (blob: Blob) => {
    if (st.failed) return; const pn = st.next++;
    try {
      await withRetry(async () => { const { url } = await post('part-url', { s3Key: st.s3Key, uploadId: st.uploadId, partNumber: pn }); const etag = await putPart(url, blob); st.parts.push({ PartNumber: pn, ETag: etag }); });
      st.sent += blob.size; onPct(Math.min(99, Math.round((st.sent / Math.max(st.total, 1)) * 100)));
    } catch (e) { fail(e); }
  };
  const flush = () => { if (st.failed || !st.buffer.length) return; const blob = new Blob(st.buffer); st.buffer = []; st.buffered = 0; st.chain = st.chain.then(() => uploadPart(blob)); };
  return {
    get failed() { return st.failed; },
    append(blob: Blob) { if (st.failed || !blob?.size) return; st.buffer.push(blob); st.buffered += blob.size; st.total += blob.size; if (st.buffered >= MIN) flush(); },
    async finish(startedAtMs: number, durationSec: number) {
      flush(); await st.chain;
      if (st.failed) throw new Error('upload failed');
      if (!st.parts.length) { fail(new Error('no parts uploaded')); throw new Error('no parts uploaded'); }
      try { await withRetry(() => post('complete', { s3Key: st.s3Key, uploadId: st.uploadId, parts: st.parts, takeId, role, startedAtMs, durationSec }), 4); }
      catch (e) { fail(e); throw e; }
      onPct(100); return st.s3Key;
    },
    abort() { if (st.s3Key && st.uploadId) post('abort', { s3Key: st.s3Key, uploadId: st.uploadId }).catch(() => {}); },
  };
}

export interface StartArgs { onFail?: (reason: string) => void; base: string; headers?: Record<string, string>; takeId: string; role: 'host' | 'guest'; includeVideo: boolean; startAtEpochMs: number; clockOffset: number; stream?: MediaStream; /* caller-owned (e.g. processedCapture) — recorded as-is, never stopped by us */ }

export function useInterviewRecorder() {
  const [recording, setRecording] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const sessRef = useRef<ReturnType<typeof createUploadSession> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef(0);

  const ownsStreamRef = useRef(true);

  const start = useCallback(async ({ base, headers = {}, takeId, role, includeVideo, startAtEpochMs, clockOffset, stream: provided, onFail }: StartArgs) => {
    setUploadFailed(false);
    const stream = provided || await navigator.mediaDevices.getUserMedia({ video: includeVideo ? { width: { ideal: 1920 }, height: { ideal: 1080 } } : false, audio: true });
    ownsStreamRef.current = !provided;
    streamRef.current = stream;
    const mime = pickRecorderMimeType();
    const ext = mime && mime.includes('mp4') ? 'mp4' : 'webm';
    const sess = createUploadSession(base, headers, takeId, role, ext, setUploadPct, (reason) => { setUploadFailed(true); onFail?.(reason); });
    sessRef.current = sess;
    startedAtRef.current = 0;
    const rec = new MediaRecorder(stream, { mimeType: mime || undefined, videoBitsPerSecond: 5_000_000 });
    recRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) { if (!startedAtRef.current) startedAtRef.current = Date.now() + clockOffset; sess.append(e.data); } };
    // Wait until the server-stamped start instant (corrected to our clock).
    const wait = Math.max(0, (startAtEpochMs - clockOffset) - Date.now());
    await new Promise(r => setTimeout(r, wait));
    rec.start(5000);
    setRecording(true); setUploadPct(0);
  }, []);

  const stop = useCallback(async () => {
    const rec = recRef.current; const sess = sessRef.current;
    recRef.current = null; sessRef.current = null;
    if (!rec) return;
    await new Promise<void>(res => { rec.onstop = () => res(); try { rec.stop(); } catch { res(); } });
    if (ownsStreamRef.current) streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setRecording(false);
    const startedAtMs = startedAtRef.current || Date.now();
    const durationSec = Math.max(0, (Date.now() - startedAtMs) / 1000);
    if (sess) { try { await sess.finish(startedAtMs, durationSec); } catch { setUploadPct(null); } }
  }, []);

  return { recording, uploadPct, uploadFailed, start, stop };
}
